import { AttachFile as AttachFileIcon } from '@mui/icons-material';
import { Box, Link, Typography } from '@mui/material';
import React, { FC } from 'react';
import Config from 'utils/Config';

export interface TimelineItem {
  id?: number | string;
  date: string;
  time?: string;
  summary: string;
  status?: string;
  createdBy?: string;
  attachmentUrl?: string;
  assignedTo?: string;
}

interface TimelineSectionProps {
  items?: TimelineItem[];
  emptyText: string;
  showStatus?: boolean;
}

const TimelineSection: FC<TimelineSectionProps> = ({ items = [], emptyText, showStatus = false }) => {
  if (!items?.length) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>{emptyText}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'relative',
        pl: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 17,
          top: 0,
          bottom: 0,
          width: '2px',
          bgcolor: 'divider',
        },
      }}>
      {items.map((item, index) => (
        <Box
          key={item.id || index}
          sx={{
            position: 'relative',
            pl: 3,
            pb: index === items.length - 1 ? 0 : 2,
          }}>
          <Box
            sx={{
              position: 'absolute',
              left: -12,
              top: 6,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              border: '2px solid white',
              boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.25)',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {item.date}
            {item.time ? ` • ${item.time}` : ''}
          </Typography>
          {showStatus && item.status && (
            <Typography variant="subtitle2" sx={{ fontSize: '0.85rem' }}>
              {item.status}
            </Typography>
          )}
          <Typography variant="body2" color="text.primary">
            {item.summary}
          </Typography>
          {item.attachmentUrl && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AttachFileIcon sx={{ fontSize: '16px', color: 'text.secondary' }} />
              <Link
                href={`${Config.BaseURL}${item.attachmentUrl}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.875rem', textDecoration: 'none' }}>
                View Attachment
              </Link>
            </Box>
          )}
          {item.assignedTo && (
            <Typography variant="caption" color="text.secondary">
              Assigned To: <strong>{item.assignedTo}</strong>
            </Typography>
          )}
          {item.createdBy && (
            <Typography variant="caption" color="text.secondary">
              {` By ${item.createdBy}`}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default TimelineSection;
